const ARTICLE_HTML_CACHE_LIMIT = 500;

const articleHtmlCache = new Map<string, string>();
const articleHtmlRequests = new Map<string, Promise<string>>();

export type WikipediaArticle = {
  id: number;
  title: string;
  description: string | null;
};

type WikiArticleJson = {
  id: number;
  name: string;
  description?: string | null;
};

function wikipediaUrl(language: string, path: string): string {
  const publicBaseUrl = import.meta.env.VITE_WIKIPEDIA_PUBLIC_URL?.replace(
    /\/+$/,
    "",
  );
  const baseUrl = publicBaseUrl || "/wiki";
  return `${baseUrl}/${encodeURIComponent(language)}${path}`;
}

export type WikipediaSearchPage = {
  articles: WikipediaArticle[];
  page: number;
  hasMore: boolean;
};

export function getRequestedGameLanguage(uiLanguage: string): string {
  return import.meta.env.VITE_WIKIPEDIA_LANGUAGE || uiLanguage;
}

function articleKey(language: string, articleId: number): string {
  return `${language}:${articleId}`;
}

function getCachedArticleHtml(key: string): string | null {
  if (!articleHtmlCache.has(key)) return null;

  const html = articleHtmlCache.get(key)!;
  articleHtmlCache.delete(key);
  articleHtmlCache.set(key, html);
  return html;
}

function setCachedArticleHtml(key: string, html: string): void {
  if (articleHtmlCache.has(key)) {
    articleHtmlCache.delete(key);
  } else if (articleHtmlCache.size >= ARTICLE_HTML_CACHE_LIMIT) {
    const oldestKey = articleHtmlCache.keys().next().value;
    if (oldestKey !== undefined) articleHtmlCache.delete(oldestKey);
  }

  articleHtmlCache.set(key, html);
}

export async function fetchWikipediaArticleHtml(
  articleId: number,
  language: string,
): Promise<string> {
  const key = articleKey(language, articleId);
  const cachedHtml = getCachedArticleHtml(key);
  if (cachedHtml !== null) return cachedHtml;

  const existingRequest = articleHtmlRequests.get(key);
  if (existingRequest) return existingRequest;

  const url = wikipediaUrl(language, `/${articleId}`);
  const request = fetch(url, {
    credentials: url.startsWith("/") ? "include" : "omit",
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`article fetch failed: ${response.status}`);
      }

      return response.text();
    })
    .then((html) => {
      setCachedArticleHtml(key, html);
      return html;
    })
    .finally(() => {
      articleHtmlRequests.delete(key);
    });

  articleHtmlRequests.set(key, request);
  return request;
}

function toArticle(article: WikiArticleJson): WikipediaArticle {
  return {
    id: article.id,
    title: article.name,
    description: article.description ?? null,
  };
}

async function fetchWikipediaJson<T>(
  language: string,
  path: string,
): Promise<T> {
  const response = await fetch(wikipediaUrl(language, path));
  if (!response.ok) {
    throw new Error(`wiki request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function searchWikipediaArticles(
  language: string,
  query: string,
  page = 1,
  limit = 10,
): Promise<WikipediaSearchPage> {
  const params = new URLSearchParams({
    q: query,
    page: String(page),
    limit: String(limit),
  });
  const result = await fetchWikipediaJson<{
    page: number;
    has_more: boolean;
    articles: WikiArticleJson[];
  }>(language, `/search?${params}`);
  return {
    articles: result.articles.map(toArticle),
    page: result.page,
    hasMore: result.has_more,
  };
}

export async function autocompleteWikipediaArticles(
  language: string,
  query: string,
): Promise<WikipediaArticle[]> {
  const params = new URLSearchParams({
    q: query,
    page: "1",
    limit: "1",
  });
  const result = await fetchWikipediaJson<{
    suggestions: WikiArticleJson[];
  }>(language, `/search?${params}`);
  return result.suggestions.map(toArticle);
}

export async function randomWikipediaArticles(
  language: string,
  limit = 5,
): Promise<WikipediaArticle[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  const result = await fetchWikipediaJson<{ articles: WikiArticleJson[] }>(
    language,
    `/random?${params}`,
  );
  return result.articles.map(toArticle);
}

if (import.meta.vitest) {
  const { afterEach, describe, expect, it, vi } = import.meta.vitest;

  afterEach(() => {
    articleHtmlCache.clear();
    articleHtmlRequests.clear();
    vi.unstubAllEnvs();
    vi.unstubAllGlobals();
  });

  describe("fetchWikipediaArticleHtml", () => {
    it("loads an article through the English Wikipedia proxy", async () => {
      const fetchMock = vi.fn().mockResolvedValue({
        ok: true,
        text: vi.fn().mockResolvedValue("<main>Douglas Adams</main>"),
      });
      vi.stubGlobal("fetch", fetchMock);

      await expect(fetchWikipediaArticleHtml(42, "en")).resolves.toBe(
        "<main>Douglas Adams</main>",
      );
      expect(fetchMock).toHaveBeenCalledWith("/wiki/en/42", {
        credentials: "include",
      });
    });

    it("loads articles directly from a configured public Wikipedia API", async () => {
      vi.stubEnv(
        "VITE_WIKIPEDIA_PUBLIC_URL",
        "https://api.wikiparty.org/wiki/",
      );
      const fetchMock = vi.fn().mockResolvedValue({
        ok: true,
        text: vi.fn().mockResolvedValue("<main>Douglas Adams</main>"),
      });
      vi.stubGlobal("fetch", fetchMock);

      await fetchWikipediaArticleHtml(42, "en");

      expect(fetchMock).toHaveBeenCalledWith(
        "https://api.wikiparty.org/wiki/en/42",
        { credentials: "omit" },
      );
    });

    it("deduplicates in-flight and cached article requests", async () => {
      const fetchMock = vi.fn().mockResolvedValue({
        ok: true,
        text: vi.fn().mockResolvedValue("<main>Article</main>"),
      });
      vi.stubGlobal("fetch", fetchMock);

      const first = fetchWikipediaArticleHtml(123, "en");
      const second = fetchWikipediaArticleHtml(123, "en");
      await Promise.all([first, second]);
      await fetchWikipediaArticleHtml(123, "en");

      expect(fetchMock).toHaveBeenCalledTimes(1);
    });
  });

  describe("searchWikipediaArticles", () => {
    it("keeps descriptions and pagination metadata from wiki-zig", async () => {
      const fetchMock = vi.fn().mockResolvedValue({
        ok: true,
        json: vi.fn().mockResolvedValue({
          page: 2,
          has_more: true,
          articles: [
            { id: 42, name: "Douglas Adams", description: "English writer" },
            { id: 43, name: "No description", description: null },
          ],
        }),
      });
      vi.stubGlobal("fetch", fetchMock);

      await expect(
        searchWikipediaArticles("en", "Douglas", 2, 10),
      ).resolves.toEqual({
        page: 2,
        hasMore: true,
        articles: [
          { id: 42, title: "Douglas Adams", description: "English writer" },
          { id: 43, title: "No description", description: null },
        ],
      });
      expect(fetchMock).toHaveBeenCalledWith(
        "/wiki/en/search?q=Douglas&page=2&limit=10",
      );
    });
  });

  describe("autocompleteWikipediaArticles", () => {
    it("reads suggestions from the combined search endpoint", async () => {
      const fetchMock = vi.fn().mockResolvedValue({
        ok: true,
        json: vi.fn().mockResolvedValue({
          language: "en",
          query: "zig",
          suggestions: [
            { id: 42, name: "Zig (programming language)" },
            { id: 43, name: "Zig compiler" },
          ],
        }),
      });
      vi.stubGlobal("fetch", fetchMock);

      await expect(autocompleteWikipediaArticles("en", "zig")).resolves.toEqual(
        [
          { id: 42, title: "Zig (programming language)", description: null },
          { id: 43, title: "Zig compiler", description: null },
        ],
      );
      expect(fetchMock).toHaveBeenCalledWith(
        "/wiki/en/search?q=zig&page=1&limit=1",
      );
    });
  });
}
